import * as React from "react";

import { cn } from "@utils/ui";

// Type for polymorphic component props
type PolymorphicProps<T extends React.ElementType> = {
  as?: T;
} & React.ComponentPropsWithoutRef<T>;

function Card<T extends React.ElementType = "div">({
  as,
  className,
  ...props
}: PolymorphicProps<T>) {
  const Component = as || "div";

  return (
    <Component
      data-slot="card"
      className={cn(
        "flex flex-col gap-6 rounded-xl border bg-background-white py-6 text-foreground shadow-sm dark:border-dark-50 dark:shadow-none",
        className
      )}
      {...props}
    />
  );
}

function CardHeader<T extends React.ElementType = "div">({
  as,
  className,
  ...props
}: PolymorphicProps<T>) {
  const Component = as || "div";

  return (
    <Component
      data-slot="card-header"
      className={cn(
        "@container/card-header has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6",
        className
      )}
      {...props}
    />
  );
}

function CardTitle<T extends React.ElementType = "div">({
  as,
  className,
  ...props
}: PolymorphicProps<T>) {
  const Component = as || "div";

  return (
    <Component
      data-slot="card-title"
      className={cn("font-semibold leading-none", className)}
      {...props}
    />
  );
}

function CardDescription<T extends React.ElementType = "div">({
  as,
  className,
  ...props
}: PolymorphicProps<T>) {
  const Component = as || "div";

  return (
    <Component
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function CardAction<T extends React.ElementType = "div">({
  as,
  className,
  ...props
}: PolymorphicProps<T>) {
  const Component = as || "div";

  return (
    <Component
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  );
}

function CardContent<T extends React.ElementType = "div">({
  as,
  className,
  ...props
}: PolymorphicProps<T>) {
  const Component = as || "div";

  return (
    <Component
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  );
}

function CardFooter<T extends React.ElementType = "div">({
  as,
  className,
  ...props
}: PolymorphicProps<T>) {
  const Component = as || "div";

  return (
    <Component
      data-slot="card-footer"
      className={cn("[.border-t]:pt-6 flex items-center px-6", className)}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
